/**
 * Static content search index — services, gallery, blog posts, top-level pages.
 * The backend only indexes products; this file supplies the rest so the site-wide
 * search bar returns a full mix (products + services + gallery + posts + pages).
 */
import { SERVICES, GALLERY } from "@/lib/site";
import { BLOG_POSTS } from "@/lib/blog";

const STATIC_PAGES = [
  { title: "Home", subtitle: "Toronto Custom Signs & Wraps", url: "/", keywords: "home landing overview" },
  { title: "About", subtitle: "Our story, values, and team", url: "/about", keywords: "team studio history experience" },
  { title: "Services", subtitle: "Full list of our services", url: "/services", keywords: "services offerings capabilities" },
  { title: "Gallery", subtitle: "Recent projects & installations", url: "/gallery", keywords: "portfolio projects photos installs" },
  { title: "Print Shop", subtitle: "Order printing with live pricing", url: "/shop", keywords: "print shop products business cards flyers online" },
  { title: "Contact", subtitle: "Get a quote or reach out", url: "/contact", keywords: "quote email phone form" },
  { title: "Blog", subtitle: "Guides & studio insights", url: "/blog", keywords: "blog articles guides" },
];

/**
 * Score how well a haystack matches a query.
 *  - exact substring match at the start of a word → high
 *  - substring match anywhere → medium
 *  - each term match adds to the score
 */
function score(haystack, query) {
  if (!haystack || !query) return 0;
  const H = haystack.toLowerCase();
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return 0;
  let s = 0;
  for (const t of terms) {
    if (!H.includes(t)) return 0;  // require ALL terms to be present
    if (H.startsWith(t)) s += 4;
    else if (H.includes(` ${t}`)) s += 3;  // word-start
    else s += 1;
  }
  return s;
}

const SERVICE_ITEMS = SERVICES.map((s) => ({
  type: "service",
  title: s.title,
  subtitle: s.short,
  url: `/services/${s.slug}`,
  image: s.image,
  keywords: [s.title, s.short, s.intro, (s.includes || []).join(" ")].join(" "),
}));

const GALLERY_ITEMS = GALLERY.map((g, i) => ({
  type: "gallery",
  title: g.title,
  subtitle: `${g.cat} project`,
  url: `/gallery#item-${i}`,
  image: g.src,
  keywords: [g.title, g.cat].join(" "),
}));

const BLOG_ITEMS = (BLOG_POSTS || []).map((p) => ({
  type: "blog",
  title: p.title,
  subtitle: p.excerpt || p.description,
  url: `/blog/${p.slug}`,
  image: p.image,
  keywords: [p.title, p.excerpt || "", p.description || "", (p.tags || []).join(" ")].join(" "),
}));

const PAGE_ITEMS = STATIC_PAGES.map((p) => ({
  type: "page",
  title: p.title,
  subtitle: p.subtitle,
  url: p.url,
  keywords: `${p.title} ${p.subtitle} ${p.keywords || ""}`,
}));

const ALL_STATIC = [...PAGE_ITEMS, ...SERVICE_ITEMS, ...GALLERY_ITEMS, ...BLOG_ITEMS];

/**
 * Search the frontend-only static content (services/gallery/blog/pages).
 * Backend search covers products separately.
 */
export function searchStatic(query, { limit = 8, exclude = new Set() } = {}) {
  const q = (query || "").trim();
  if (q.length < 1) return [];
  const scored = [];
  for (const item of ALL_STATIC) {
    if (exclude.has(item.type)) continue;
    const s = Math.max(score(item.title, q) * 2, score(item.keywords, q));
    if (s > 0) scored.push({ ...item, _score: s });
  }
  scored.sort((a, b) => b._score - a._score);
  return scored.slice(0, limit).map(({ _score: _s, ...rest }) => rest);
}
